import React, { FC, Dispatch, SetStateAction } from "react";
import EditIcon from "@mui/icons-material/Edit";
import { Typography, Tooltip, Checkbox } from "@mui/material";
import getUnitLetter from "../../util/letters";
import DotsModal from "../RemoveEditDuplicate/Dots/DotsModal";
import Dots from "../RemoveEditDuplicate/Dots/Dots";
import KdModal from "../RemoveEditDuplicate/KD/KdModal";
import "../RemoveEditDuplicate/EditBtn.css";
import { UnitObj } from "../../util/quoteCartStorage";
import { styled } from "@mui/material/styles";
import useUnitActionsSelector from "./useUnitActionsSelector";

const StyledCheckboxHover = styled(Checkbox)({
  "&:hover": {
    backgroundColor: "transparent",
  },
  padding: 0,
});

const StyledSpanCheck = styled("span")({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  "$root.Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2,
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background: "rgba(206,217,224,.5)",
  },
  backgroundColor: "red",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&:before": {
    display: "block",
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: "#C12807",
  },
});

const StyledSpan = styled("span")({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow:
    "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: "#f5f8fa",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
  "$root.Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2,
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background: "rgba(206,217,224,.5)",
  },
});

const StyledTypography = styled(Typography)({
  fontWeight: "bold",
});

const StyledDivCol = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingLeft: 10,
  paddingRight: 10,
  "&:hover": {
    cursor: "pointer",
  },
});

function StyledCheckbox(props: any) {
  return (
    <StyledCheckboxHover
      color="default"
      size="small"
      checkedIcon={<StyledSpanCheck />}
      icon={<StyledSpan />}
      inputProps={{ "aria-label": "decorative checkbox" }}
      {...props}
    />
  );
}

interface UnitActionsProps {
  setWindowOrDoor: Dispatch<SetStateAction<string>>;
  // setWindowVal: Dispatch<SetStateAction<string>>;
  loadDuplicate: boolean;
  duplicateUnit: (unitId: string) => void;
  index: number;
  unit: UnitObj;
  setEditQuote: Dispatch<SetStateAction<boolean>>;
  quoteId: string;
  handleDeleteCheck: (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => void;
  showChecks: boolean;
  hoverId: string;
  status: string;
  isEditDisabled: boolean;
}

const UnitActions: FC<UnitActionsProps> = ({
  setWindowOrDoor,
  // setWindowVal,
  loadDuplicate,
  duplicateUnit,
  index,
  unit,
  setEditQuote,
  quoteId,
  handleDeleteCheck,
  showChecks,
  hoverId,
  status,
  isEditDisabled
}) => {
  const {
    InEditState,
    addCustomGlass,
    getUnit,
    handleOpenKD,
    handleCloseKD,
    handleOpen,
    handleCheck,
    error,
    modalType,
    loading,
    open,
    openKD,
    isEdit,
    handleClose,
    switchChecked,
  } = useUnitActionsSelector(quoteId, unit, setEditQuote, setWindowOrDoor);

  return (
    <>
      <DotsModal
        handleClose={handleClose}
        loading={loading}
        clickFn={modalType === "edit" ? getUnit : addCustomGlass}
        open={open}
        modalType={modalType}
        error={error}
        checked={switchChecked}
        handleCheck={handleCheck}
      />

      <KdModal
        open={openKD}
        handleClose={handleCloseKD}
        unitId={unit._id || ""}
        doorType={unit.doorType}
        quoteId={quoteId}
        kd={unit.kd || false}
      />

        <StyledDivCol>
          <StyledTypography>{getUnitLetter(index)}</StyledTypography>

          <Tooltip title={isEditDisabled ? "Restricted Unit" : "Edit Unit"} enterDelay={500} placement="top">
            <div>
              <EditIcon
                onClick={
                  isEditDisabled ? () => {} : !isEdit ? () => handleOpen("edit") : () => InEditState()
                }
                style={{ color: isEditDisabled ? "#808080" : "#57d5f2" }}
                id={unit.required?.length ? "editBtn" : ""}
              />
            </div>
          </Tooltip>

          {!isEditDisabled && <div style={{height: 24}}>
            <Dots
              openModal={handleOpen}
              duplicateUnit={duplicateUnit}
              loading={loadDuplicate}
              unitId={unit._id || ""}
              doorType={unit.doorType}
              openModalKD={handleOpenKD}
              kd={unit.kd || false}
              status={status}
            />
          </div>}

          {(hoverId === unit._id || showChecks) && !isEditDisabled && (
            <div>
              <StyledCheckbox
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleDeleteCheck(e, unit._id || "")
                }
              />
            </div>
           )} 
        </StyledDivCol>
    </>
  );
};

export default UnitActions;
